/**
 * The four general-purpose letters the module has always shipped with.
 *
 * They belong to no campaign. Each one is a SHAPE - a plain note, a findings
 * report, a two-column offer, a follow-up - that an operator fills with their
 * own case, and the sample findings in them are placeholders, not claims.
 *
 * The set is deliberately small and deliberately plain. Cold outreach that
 * arrives looking like a newsletter reads as bulk mail and gets treated as bulk
 * mail, so the first preset is a letter with no image, no banner and no columns,
 * and the decorated ones exist for the cases where the recipient already knows
 * who we are. `docs/crm-turundus-plaan.md` section 10 wrote that reasoning down
 * before any of this was built; it still holds.
 */
import { newBlockId } from "../types";
import { b, design, footer, pick, PLAIN_PAGE, signature, type L, type Preset } from "./shared";

const PLAIN_BODY: L = {
  et:
    "<p>Tere,</p>" +
    "<p>Vaatasin {{company}} kodulehte ja jäi silma paar asja, mida tasuks üle vaadata. Kui huvi on, saadan lühikese kokkuvõtte.</p>",
  en:
    "<p>Hello,</p>" +
    "<p>I had a look at the {{company}} website and a couple of things stood out. If it is of interest, I can send a short summary.</p>",
};
const PLAIN_FINDINGS: L = {
  et: "Sisuhaldus on vana versiooni peal\nOsa pluginaid on uuendamata\nKontaktivorm ei ole krüpteeritud",
  en: "The CMS is on an old version\nSeveral plugins are out of date\nThe contact form is not encrypted",
};
const PLAIN_CLOSE: L = {
  et: "<p>Kui see teema praegu päevakorral ei ole, siis öelge julgelt - rohkem ei tülita.</p>",
  en: "<p>If this is not on your agenda right now, just say so and I will not write again.</p>",
};

const AUDIT_INTRO: L = {
  et: "<p>Käisime {{domain}} avalikult üle. Allpool on see, mis silma jäi. Iga rea taga on mõõdetud number ja aadress, kust see tuli.</p>",
  en: "<p>We looked over {{domain}} from the outside. Below is what stood out. Every line has a measured number behind it, and the address it came from.</p>",
};
const AUDIT_FINDINGS: L = {
  et: "Tuumik: 3 suurt versiooni maas\nPluginad: 9 uuendamata\nTLS: kontaktivorm ilma HTTPS-ita",
  en: "Core: three major versions behind\nPlugins: nine out of date\nTLS: contact form without HTTPS",
};

const OFFER_INTRO: L = {
  et: '<p style="text-align:center">Kolisime {{company}} sarnaseid ettevõtteid Huslysse ilma vahepealse seisakuta.</p>',
  en: '<p style="text-align:center">We have moved companies like {{company}} onto Husly with no downtime in between.</p>',
};
const OFFER_BEFORE: L = {
  et: "Uuendamata tuumik\nAeglane mobiilis\nSisu muutmine käib meilitsi",
  en: "Core not updated\nSlow on a phone\nContent changes go through email",
};
const OFFER_AFTER: L = {
  et: "Uuendused automaatselt\nMobiilis kiire\nSisu muudad ise",
  en: "Updates run themselves\nFast on a phone\nYou change the content",
};

const FOLLOWUP_BODY: L = {
  et:
    "<p>Tere,</p>" +
    "<p>Saatsin paar nädalat tagasi {{company}} kodulehe kohta lühikese ülevaate. Kui see kaduma läks, saadan uuesti - ja kui teema ei ole aktuaalne, siis rohkem ei kirjuta.</p>",
  en:
    "<p>Hello,</p>" +
    "<p>A couple of weeks ago I sent a short review of the {{company}} website. If it got lost, I can send it again - and if this is not relevant, I will not write further.</p>",
};

export const STARTER_PRESETS: Preset[] = [
  {
    id: "plain-letter",
    key: "plain",
    tone: "plain",
    subject: { et: "Paar tähelepanekut {{company}} kodulehe kohta", en: "A couple of notes on the {{company}} website" },
    build: (locale) =>
      design(
        [
          b("text", { html: pick(PLAIN_BODY, locale) }),
          b("list", { items: pick(PLAIN_FINDINGS, locale) }),
          b("text", { html: pick(PLAIN_CLOSE, locale) }),
          signature(),
          b("divider"),
          footer(locale),
        ],
        PLAIN_PAGE,
      ),
  },
  {
    id: "audit-report",
    key: "audit",
    tone: "report",
    subject: { et: "{{company}} kodulehe kiirülevaade", en: "A quick review of the {{company}} website" },
    build: (locale) =>
      design([
        b("heading", {
          text: locale === "en" ? "A quick review of {{company}}" : "{{company}} kodulehe kiirülevaade",
          level: "h2",
        }),
        b("text", { html: pick(AUDIT_INTRO, locale) }),
        b("quote", {
          text:
            locale === "en"
              ? "The CMS core is three major versions behind."
              : "Sisuhaldus on kolm suurt versiooni maas.",
          author: locale === "en" ? "Automated scan" : "Automaatne skann",
        }),
        b("list", { items: pick(AUDIT_FINDINGS, locale) }),
        b("button", {
          text: locale === "en" ? "See the full picture" : "Vaata täispilti",
          url: "https://husly.app",
          align: "left",
        }),
        signature(),
        b("divider"),
        footer(locale),
      ]),
  },
  {
    id: "offer",
    key: "offer",
    tone: "offer",
    subject: { et: "Uus koduleht kahe nädalaga", en: "A new website in two weeks" },
    build: (locale) =>
      design([
        b("heading", {
          text: locale === "en" ? "A new website in two weeks" : "Uus koduleht kahe nädalaga",
          level: "h2",
          align: "center",
        }),
        b("text", { html: pick(OFFER_INTRO, locale), align: "center" }),
        b("columns", {
          gap: 24,
          cols: [
            {
              id: newBlockId(),
              children: [
                b("heading", { text: locale === "en" ? "Today" : "Praegu", level: "h3" }),
                b("list", { items: pick(OFFER_BEFORE, locale) }),
              ],
            },
            {
              id: newBlockId(),
              children: [
                b("heading", { text: locale === "en" ? "After" : "Pärast", level: "h3" }),
                b("list", { items: pick(OFFER_AFTER, locale) }),
              ],
            },
          ],
        }),
        b("button", {
          text: locale === "en" ? "Fifteen minutes to talk it over" : "Räägime 15 minutit",
          url: "https://husly.app",
          align: "center",
        }),
        b("divider"),
        footer(locale),
      ]),
  },
  {
    id: "follow-up",
    key: "followUp",
    tone: "followup",
    subject: { et: "Kas see jõudis kohale?", en: "Did this reach you?" },
    build: (locale) =>
      design(
        [
          b("text", { html: pick(FOLLOWUP_BODY, locale) }),
          b("button", {
            text: locale === "en" ? "Send the review again" : "Saada ülevaade uuesti",
            url: "https://husly.app",
            align: "left",
          }),
          signature(),
          b("divider"),
          footer(locale),
        ],
        PLAIN_PAGE,
      ),
  },
];
